import { useAuth0 } from "@auth0/auth0-react";
import { BoxProps, Flex } from "@chakra-ui/layout";
import {
  Box,
  Button,
  Icon,
  Link,
  MenuItem,
  Skeleton,
  Spacer,
} from "@chakra-ui/react";
import _ from "lodash";
import React, { ReactNode, useMemo } from "react";
import {
  RiArrowDownSLine,
  RiBuildingLine,
  RiUserSettingsLine,
} from "react-icons/ri";
import { NavLink, useNavigate } from "react-router-dom";
import { useOrganization } from "src/hooks/useOrganization";
import { useAdminOrganizations } from "src/hooks/useOrganizations";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import { useUserProfile } from "src/hooks/useUserProfile";
import { useMyProfileHomePage } from "src/scenes/orgAdmin/myProfile/useMyProfileHomePage";
import * as Url from "src/services/url";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import { GenericError } from "../Feedback/GenericError";
import { WithFeatureFlags } from "../Permissions/WithFeatureFlags";
import { WithUserPermissions } from "../Permissions/WithUserPermissions";
import { SearchableMenuPopover } from "../Popovers/SearchableMenuPopover";
import { useFlagsRemoteData } from "../Providers/FeatureFlagProvider";
import { HighlightedText } from "../Text/HighlightedText";
import { AdminMenu } from "./AdminMenu";
import { RemoteDataView } from "./RemoteDataView";

const AVELA_ADMIN_KEY = "__avela_admin__";
interface HeaderProps extends BoxProps {
  logo: ReactNode;
}

export const Header = ({
  logo,
  ...restProps
}: HeaderProps): React.ReactElement | null => {
  const { isAuthenticated, isLoading, error } = useAuth0();
  const hasAdminPermission = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.ORG_ADMIN,
    HasuraRole.DISTRICT_ADMIN,
    HasuraRole.SCHOOL_ADMIN,
  ]);
  const hasAvelaAdminPermission = useRequiredHasuraRoles([HasuraRole.ADMIN]);
  const userProfile = useUserProfile();
  const organizations = useAdminOrganizations();
  const organization = useOrganization();
  const flagsRD = useFlagsRemoteData(["org-config-self-service"]);

  const remoteData: typeof flagsRD = useMemo(() => {
    if (error !== undefined) {
      return RD.failure(error);
    }

    if (isLoading) {
      return RD.loading();
    }

    return flagsRD;
  }, [error, isLoading, flagsRD]);

  const myProfileRD = useMyProfileHomePage();

  return (
    <Flex
      as="header"
      width="100%"
      height="100%"
      backgroundColor="primary.500"
      align="center"
      paddingX={4}
      gap={4}
      {...restProps}
    >
      <RemoteDataView
        remoteData={remoteData}
        error={(error) => <GenericError error={error} />}
      >
        {(flags) => {
          return (
            <>
              {logo}
              {hasAdminPermission && (
                <RemoteDataView
                  remoteData={organizations}
                  loading={<Skeleton width="15rem" height="2.5rem" />}
                  error={(error) => <GenericError error={error} />}
                >
                  {(orgs) => {
                    if (orgs.length <= 1) {
                      return null;
                    }
                    return (
                      <OrgSwitcherMenu
                        orgs={orgs}
                        organization={organization}
                        hasAvelaAdminPermission={hasAvelaAdminPermission}
                      />
                    );
                  }}
                </RemoteDataView>
              )}

              {isAuthenticated ? (
                <>
                  <Spacer />
                  <AdminMenu person={userProfile.toNullable()}>
                    <RemoteDataView
                      remoteData={organization}
                      error={() => <GenericError variant="inline" />}
                      loading={<Skeleton width="100%" height="4rem" />}
                      reloading={null}
                    >
                      {(org) => (
                        <>
                          <RemoteDataView
                            remoteData={myProfileRD}
                            error={() => <GenericError variant="inline" />}
                            loading={<Skeleton width="100%" height="2rem" />}
                            reloading={null}
                          >
                            {(myProfile) =>
                              myProfile !== "not-found" ? (
                                <MenuItem
                                  px={4}
                                  as={NavLink}
                                  to={Url.OrgAdmin.UserProfile.home(org)}
                                  gap={2}
                                >
                                  <Icon as={RiUserSettingsLine} />
                                  <Box>Profile</Box>
                                </MenuItem>
                              ) : null
                            }
                          </RemoteDataView>
                          <WithFeatureFlags
                            featureFlags={["org-config-self-service"]}
                          >
                            <WithUserPermissions
                              permissions={[
                                "navigation.admin.organization_settings",
                              ]}
                            >
                              <MenuItem
                                paddingX={4}
                                as={NavLink}
                                to={Url.OrgAdmin.Org.settings(org)}
                                gap={2}
                              >
                                <Icon as={RiBuildingLine} />
                                <Box>Organization</Box>
                              </MenuItem>
                            </WithUserPermissions>
                          </WithFeatureFlags>
                        </>
                      )}
                    </RemoteDataView>
                  </AdminMenu>
                </>
              ) : null}
            </>
          );
        }}
      </RemoteDataView>
    </Flex>
  );
};

type Organization = { id: string; name: string; path: string };

type OrgSwitcherItem = {
  key: string;
  // label concatenates name + path so the shared component's built-in
  // label-substring filter matches either; renderItem shows only displayName.
  label: string;
  displayName: string;
  url: string;
};

const OrgSwitcherMenu: React.FC<{
  orgs: Organization[];
  organization: ReturnType<typeof useOrganization>;
  hasAvelaAdminPermission: boolean;
}> = ({ orgs, organization, hasAvelaAdminPermission }) => {
  const navigate = useNavigate();

  const items = useMemo<OrgSwitcherItem[]>(() => {
    const orgItems: OrgSwitcherItem[] = _.sortBy(orgs, "name").map((org) => ({
      key: org.id,
      label: `${org.name} ${org.path}`,
      displayName: org.name,
      url: Url.OrgAdmin.index(org, "page=login"),
    }));
    if (hasAvelaAdminPermission) {
      return [
        {
          key: AVELA_ADMIN_KEY,
          label: "Avela Admin",
          displayName: "Avela Admin",
          url: "/admin",
        },
        ...orgItems,
      ];
    }
    return orgItems;
  }, [orgs, hasAvelaAdminPermission]);

  return (
    <SearchableMenuPopover<OrgSwitcherItem>
      items={items}
      searchPlaceholder="Search by name or path"
      popoverTrigger={
        <Button
          background="none"
          _hover={{ background: "none" }}
          _active={{ background: "none" }}
          color="white"
          rightIcon={<RiArrowDownSLine />}
        >
          {organization
            .map((org) => org.name)
            .withDefault(hasAvelaAdminPermission ? "Avela Admin" : "")}
        </Button>
      }
      renderItem={(item, keyword) => (
        <Link href={item.url}>
          <HighlightedText invert text={item.displayName} keyword={keyword} />
        </Link>
      )}
      onItemSelected={(item) => {
        if (item.key === AVELA_ADMIN_KEY) {
          navigate(item.url);
        } else {
          // Full page reload preserves the pre-existing org-switch behavior
          // (reloadDocument on the old Menu-based switcher).
          window.location.href = item.url;
        }
      }}
    />
  );
};
